/**
 * Keeps an RX bundle behaving like one item in the cart.
 *
 * The lens and the frame are two line items tied by _bundleHash. The theme
 * renders them as one card (see cart-line-items.liquid); this makes the card's
 * controls act on both lines, so removing or re-counting a bundle can never
 * leave a lone lens or a frame without lenses behind.
 *
 * Listens on the document because the drawer and the cart page both re-render
 * their markup, and a listener bound to a card would not survive that.
 */
(function initRxCartBundle() {
  if (window.__rxCartBundleInit) return;
  window.__rxCartBundleInit = true;

  const keysOf = (card) => (card?.dataset.rxBundleKeys || '').split(',').filter(Boolean);

  /**
   * Current quantity of the bundle, read from the cart rather than from the
   * input: the theme re-renders the card after an update but leaves the field
   * showing the previous number, so trusting it turned "one less" into zero.
   */
  async function currentQuantity(keys) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const line = cart.items?.find((item) => keys.includes(item.key));
      return line ? line.quantity : 1;
    } catch {
      return 1;
    }
  }

  async function stepBundle(keys, delta) {
    const quantity = (await currentQuantity(keys)) + delta;
    await updateBundle(keys, Math.max(0, quantity));
  }

  async function updateBundle(keys, quantity) {
    const updates = {};
    for (const key of keys) updates[key] = quantity;

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });

    // Whatever the outcome, the cart on screen has to match the cart on the
    // server again — refresh even on failure.
    for (const name of ['theme:cart:refresh', 'theme:cart:change']) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    }
    // Broadcast 5.x has no refresh event; its cart element redraws on demand.
    for (const element of document.querySelectorAll('cart-items, cart-drawer-items')) {
      if (typeof element.getCart === 'function') element.getCart();
    }
    if (!response.ok) console.warn('[rx] bundle cart update failed', response.status);
  }

  document.addEventListener(
    'click',
    (event) => {
      const card = event.target.closest?.('.cart__item--rx-bundle');
      if (!card) return;

      const keys = keysOf(card);
      if (keys.length < 2) return; // nothing to keep in step

      const remove = event.target.closest('[data-item-remove]');
      if (remove) {
        event.preventDefault();
        event.stopPropagation();
        updateBundle(keys, 0);
        return;
      }

      const step = event.target.closest('[name="increase"], [name="decrease"]');
      if (!step) return;

      event.preventDefault();
      event.stopPropagation();
      stepBundle(keys, step.name === 'increase' ? 1 : -1);
    },
    // Capture: the theme binds its own handlers to these controls, and they
    // would otherwise update only the line the card was rendered from.
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const card = event.target.closest?.('.cart__item--rx-bundle');
      if (!card || !event.target.matches('.cart__quantity-field')) return;

      const keys = keysOf(card);
      if (keys.length < 2) return;

      event.preventDefault();
      event.stopPropagation();
      updateBundle(keys, Math.max(0, Number(event.target.value) || 0));
    },
    true
  );
})();
